import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Tier } from '@backrs_ui/common-ui';

import { CareerPathWithScore, CareerUserResponseSet } from '@backrs_ui/grow-ui/src/models/models';
import { BlueprintAspirationsCategories } from '@backrs_ui/grow-ui/src/redux/blueprint/blueprint.lubav.api';

import { SharedResourceType, SharedSolutionSessionData } from './sharedSession.api';
import type { RootState } from '../store';

export type SharedBlueprintData = Readonly<{
  tier: Tier;
  careerPathSuggestions: CareerPathWithScore[];
  trackCode: string;
  userResponseSet: CareerUserResponseSet;
  entrepreneurshipRecommended: boolean;
  aspirationsCategories?: BlueprintAspirationsCategories;
}>;

export type SharedSessionState = Readonly<{
  active: boolean;
  token: string | null;
  resourceType: SharedResourceType | null;
  resourceId: number | null;
  blueprintData: SharedBlueprintData | null;
  solutionSessionData: SharedSolutionSessionData | null;
  ownerFirstName: string;
  ownerLastName: string;
  ownerProfileImageUrl: string | null;
}>;

const initialState: SharedSessionState = {
  active: false,
  token: null,
  resourceType: null,
  resourceId: null,
  blueprintData: null,
  solutionSessionData: null,
  ownerFirstName: '',
  ownerLastName: '',
  ownerProfileImageUrl: null,
};

export const sharedSessionSlice = createSlice({
  name: 'sharedSession',
  initialState,
  reducers: {
    activateSharedSession: (
      state,
      action: PayloadAction<{
        token: string;
        ownerFirstName: string;
        ownerLastName: string;
        ownerProfileImageUrl: string | null;
        resourceType?: SharedResourceType;
        resourceId?: number;
      }>,
    ) => {
      state.active = true;
      state.token = action.payload.token;
      state.ownerFirstName = action.payload.ownerFirstName;
      state.ownerLastName = action.payload.ownerLastName;
      state.ownerProfileImageUrl = action.payload.ownerProfileImageUrl;
      state.resourceType = action.payload.resourceType || null;
      state.resourceId = action.payload.resourceId || null;
    },

    deactivateSharedSession: () => {
      return initialState;
    },

    updateSharedBlueprintData: (state, action: PayloadAction<SharedBlueprintData>) => {
      state.blueprintData = action.payload;
    },

    updateSharedSolutionSessionData: (state, action: PayloadAction<SharedSolutionSessionData>) => {
      state.solutionSessionData = action.payload;
    },
  },
});

export const {
  activateSharedSession,
  deactivateSharedSession,
  updateSharedBlueprintData,
  updateSharedSolutionSessionData,
} = sharedSessionSlice.actions;

// Selectors
export const selectSharedSession = (state: RootState) => state.sharedSession;
export const selectIsSharedSessionActive = (state: RootState) => state.sharedSession.active;
export const selectShareToken = (state: RootState) => state.sharedSession.token;
export const selectSharedResourceType = (state: RootState) => state.sharedSession.resourceType;
export const selectSharedResourceId = (state: RootState) => state.sharedSession.resourceId;
export const selectSharedBlueprintData = (state: RootState) => state.sharedSession.blueprintData;
export const selectSharedSolutionSessionData = (state: RootState) =>
  state.sharedSession.solutionSessionData;
export const selectSharedSessionOwnerFirstName = (state: RootState) =>
  state.sharedSession.ownerFirstName;
export const selectSharedSessionOwnerLastName = (state: RootState) =>
  state.sharedSession.ownerLastName;
export const selectSharedSessionOwnerProfileImageUrl = (state: RootState) =>
  state.sharedSession.ownerProfileImageUrl;
export const selectSharedSessionOwnerFullName = createSelector(
  [selectSharedSessionOwnerFirstName, selectSharedSessionOwnerLastName],
  (firstName, lastName) => {
    const fullName = `${firstName} ${lastName}`.trim();

    return fullName || undefined;
  },
);
